import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Trash2 } from 'lucide-react';
import type { EntityAppliedIn, EntityGroupListItem } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import {
  APPLIED_IN_LABELS,
  APPLIED_IN_TABS,
  ENTITIES_QUERY_KEY,
  entityGroupsOptions,
} from './entities-queries';
import { EntityGroupDialog } from './entity-group-dialog';

const TAB_TRIGGER_CLASSES =
  'rounded-none border-b-2 border-transparent px-3 pb-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none';

/**
 * /settings/entities — Campos personalizados (referência tiflux-56):
 * abas por alvo (Ticket | Recurso | Cliente), busca client-side,
 * tabela Nome | Mesas | Quantidade de campos | Status | excluir.
 */
export function EntitiesPage() {
  const [tab, setTab] = useState<EntityAppliedIn>('ticket');

  return (
    <div className="space-y-4">
      <div>
        <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/settings" className="transition-colors hover:text-foreground">
            Configurações
          </Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Campos personalizados</span>
        </nav>
        <h1 className="mt-2 text-xl leading-7 font-semibold">Campos personalizados</h1>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        <Tabs value={tab} onValueChange={(v) => setTab(v as EntityAppliedIn)}>
          <div className="border-b px-4 pt-3">
            <TabsList className="h-auto gap-1 bg-transparent p-0">
              {APPLIED_IN_TABS.map((appliedIn) => (
                <TabsTrigger key={appliedIn} value={appliedIn} className={TAB_TRIGGER_CLASSES}>
                  {APPLIED_IN_LABELS[appliedIn]}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {APPLIED_IN_TABS.map((appliedIn) => (
            <TabsContent key={appliedIn} value={appliedIn}>
              <GroupsTab appliedIn={appliedIn} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

// ── Uma aba (um applied_in): busca + botão + tabela ─────────────────────────

function GroupsTab({ appliedIn }: { appliedIn: EntityAppliedIn }) {
  const queryClient = useQueryClient();
  const list = useQuery(entityGroupsOptions());
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<{ groupId: number | null } | null>(null);

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/entities/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ENTITIES_QUERY_KEY }),
  });

  const all = (list.data ?? []).filter((g) => g.appliedIn === appliedIn);
  const term = search.trim().toLowerCase();
  const groups = term ? all.filter((g) => g.name.toLowerCase().includes(term)) : all;
  const showDesks = appliedIn === 'ticket';
  const label = APPLIED_IN_LABELS[appliedIn];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
        <h2 className="text-base font-semibold">{label}</h2>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar"
              aria-label={`Buscar grupo de campos de ${label.toLowerCase()}`}
              className="h-9 w-44 rounded-md border border-border bg-card pr-8 pl-3 text-sm text-foreground transition-colors outline-none placeholder:text-muted-foreground/70 focus:border-primary sm:w-56"
            />
            <Search className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          <Button size="sm" onClick={() => setDialog({ groupId: null })}>
            <Plus className="size-4" strokeWidth={2.5} />
            Campo personalizado
          </Button>
        </div>
      </div>

      {remove.error ? (
        <p className="px-4 pt-2 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}

      {list.isPending ? (
        <div className="space-y-2 p-4" aria-busy>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : list.isError ? (
        <EmptyState message={`Erro ao carregar campos personalizados: ${errorMessage(list.error)}`} />
      ) : groups.length === 0 ? (
        <EmptyState
          className="h-52"
          message={
            term
              ? 'Nenhum grupo encontrado para a busca'
              : `Nenhum grupo de campos de ${label.toLowerCase()} cadastrado`
          }
        />
      ) : (
        <div className="overflow-x-auto p-4 pt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-10 px-4 font-semibold">Nome</th>
                {showDesks ? <th className="h-10 px-4 font-semibold">Mesas</th> : null}
                <th className="h-10 px-4 font-semibold">Quantidade de campos</th>
                <th className="h-10 px-4 font-semibold">Status</th>
                <th className="h-10 w-14 px-4 font-semibold">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <GroupRow
                  key={g.id}
                  group={g}
                  showDesks={showDesks}
                  onOpen={() => setDialog({ groupId: g.id })}
                  onRemove={() => {
                    if (
                      window.confirm(`Excluir o grupo "${g.name}" e todos os seus campos e valores?`)
                    ) {
                      remove.mutate(g.id);
                    }
                  }}
                  removing={remove.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog ? (
        <EntityGroupDialog
          appliedIn={appliedIn}
          groupId={dialog.groupId}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

function GroupRow({
  group,
  showDesks,
  onOpen,
  onRemove,
  removing,
}: {
  group: EntityGroupListItem;
  showDesks: boolean;
  onOpen: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  return (
    <tr className="h-10 border-b border-divider last:border-0">
      <td className="max-w-72 px-4">
        <button
          type="button"
          onClick={onOpen}
          className="max-w-full truncate font-medium transition-colors hover:text-primary"
        >
          {group.name}
        </button>
      </td>
      {showDesks ? (
        <td className="max-w-80 truncate px-4 text-muted-foreground">
          {group.deskNames.length > 0 ? group.deskNames.join(', ') : 'Todas'}
        </td>
      ) : null}
      <td className="px-4 text-muted-foreground tabular-nums">{group.fieldCount}</td>
      <td className="px-4 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5">
          <span
            className={cn('size-2 rounded-full', group.active ? 'bg-success' : 'bg-border')}
            aria-hidden
          />
          {group.active ? 'Ativo' : 'Inativo'}
        </span>
      </td>
      <td className="px-4">
        <div className="flex justify-end">
          <DeskRowAction label="Excluir grupo" destructive disabled={removing} onClick={onRemove}>
            <Trash2 className="size-3.5" />
          </DeskRowAction>
        </div>
      </td>
    </tr>
  );
}
